import {
  parseEntityRef,
  stringifyEntityRef,
  Entity,
} from '@backstage/catalog-model';
import {
  Snapshot,
  Product,
  Software,
  Repository,
  Relationship,
  Provenance,
  Evidence,
  Scope,
  Question,
  ReviewAnswer,
} from './types';
import {
  CATALOG_ANNOTATIONS,
  CATALOG_PORTFOLIO,
  annotation,
  catalogProvenance,
  curatedProvenance,
  isSystem,
  joinField,
  normalizeCode,
  systemsByCode,
} from './catalogLayer';

export const shortName = (name: string) => name.replace(/^Univerus\s+/, '');
export function evidenceOf(p?: Provenance): Evidence {
  if (p?.verification === 'confirmed' && p.lastReviewed) return 'confirmed';
  if (p?.verification === 'declared') return 'stated';
  return p?.verification === 'extracted' ? 'suggested' : 'unknown';
}
export function safeWebUrl(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(
      /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`,
    );
    return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}
export function repositoryUrl(repo: {
  slug: string;
  home?: string;
  url?: string;
  adoSource?: string;
}): string {
  if (repo.url && safeWebUrl(repo.url)) return safeWebUrl(repo.url)!;
  if (repo.home === 'ado') {
    const [org, project, ...name] = (repo.adoSource || repo.slug).split('/');
    return `https://dev.azure.com/${encodeURIComponent(
      org,
    )}/${encodeURIComponent(project)}/_git/${encodeURIComponent(
      name.join('/'),
    )}`;
  }
  return `https://github.com/${repo.slug
    .split('/')
    .map(encodeURIComponent)
    .join('/')}`;
}
/** An Azure DevOps repository, lower-cased. `org` is absent when unknown. */
export interface AdoRepoKey {
  org?: string;
  project: string;
  repo: string;
}
const adoSegment = (value: string) => {
  try {
    return decodeURIComponent(value).toLowerCase();
  } catch {
    return value.toLowerCase();
  }
};
/**
 * Parses an Azure DevOps location: an `org/project/repo` slug, or a
 * (`url:`-prefixed) dev.azure.com or legacy *.visualstudio.com git URL with
 * any trailing slash, sub-path or query.
 */
export function adoRepoKey(value?: string): AdoRepoKey | undefined {
  const raw = value?.trim().replace(/^url:/i, '');
  if (!raw) return undefined;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
    const parts = raw.split('/').filter(Boolean).map(adoSegment);
    if (parts.length !== 3) return undefined;
    return { org: parts[0], project: parts[1], repo: parts[2] };
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }
  const host = url.hostname.toLowerCase();
  const segments = url.pathname.split('/').filter(Boolean).map(adoSegment);
  let org: string | undefined;
  if (host === 'dev.azure.com') org = segments.shift();
  else if (host.endsWith('.visualstudio.com')) {
    org = host.slice(0, -'.visualstudio.com'.length);
    if (segments[0] === 'defaultcollection') segments.shift();
  } else return undefined;
  const git = segments.indexOf('_git');
  if (!org || git < 0 || !segments[git + 1]) return undefined;
  const repo = segments[git + 1];
  // A project's default repository has no project segment before _git.
  const project = git === 0 ? repo : segments[git - 1];
  return { org, project, repo };
}
/** Whether a catalog entity's annotations point at this ADO repository. */
export function matchesAdoRepo(
  annotations: Record<string, string>,
  target: AdoRepoKey,
): boolean {
  const same = (key?: AdoRepoKey) =>
    !!key &&
    key.project === target.project &&
    key.repo === target.repo &&
    (!key.org || !target.org || key.org === target.org);
  if (same(adoRepoKey(annotations['backstage.io/source-location'])))
    return true;
  // dev.azure.com/project-repo is "project/repo"; the org, when recorded,
  // lives in dev.azure.com/host-org as "dev.azure.com/org".
  const [project, repo, ...rest] = (
    annotations['dev.azure.com/project-repo'] || ''
  )
    .split('/')
    .map(adoSegment);
  if (!project || !repo || rest.length) return false;
  const hostOrg = annotations['dev.azure.com/host-org']
    ?.split('/')
    .filter(Boolean)
    .pop();
  return same({
    org: hostOrg ? adoSegment(hostOrg) : undefined,
    project,
    repo,
  });
}
export const repositoryId = (repo: { slug: string; home?: string }) =>
  `${repo.home === 'ado' ? 'ado' : 'github'}:${repo.slug.toLowerCase()}`;
/** Where the repository lives, as shown in every repository list. */
export function repositoryHost(repo: { home?: string }): string {
  if (repo.home === 'ado') return 'Azure DevOps';
  if (repo.home === 'ado-mirror') return 'GitHub · ADO mirror';
  return 'GitHub';
}
export function compareVersions(a: Software, b: Software): number {
  const av = (a.versions || []).join('/');
  const bv = (b.versions || []).join('/');
  if (!av || !bv)
    return (() => {
      if (av) {
        return -1;
      }
      if (bv) {
        return 1;
      }
      return a.label.localeCompare(b.label);
    })();
  return (
    bv.localeCompare(av, undefined, { numeric: true }) ||
    a.label.localeCompare(b.label)
  );
}
const provenanceParts = (p?: Provenance) => [
  p?.source,
  p?.verification,
  p?.lastReviewed,
];
/**
 * A question's revision: a hash of only the record it asks about, so a saved
 * answer goes stale when that mapping changes, not on every snapshot refresh.
 */
export function questionRevision(
  ...parts: (string | null | undefined)[]
): string {
  // FNV-1a, 32-bit. Stable across browsers; not for security.
  let hash = 0x811c9dc5;
  for (const char of JSON.stringify(parts.map(part => part ?? null))) {
    hash ^= char.codePointAt(0)!;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `q1:${hash.toString(16).padStart(8, '0')}`;
}
export function normalizeProducts(
  snapshot: Snapshot,
  entities: Entity[],
): Product[] {
  const entitiesByRef = new Map(
    entities.map(entity => [stringifyEntityRef(entity), entity]),
  );
  const byCode = systemsByCode(entities);
  const systemNamed = (name: string) => {
    const entity = entitiesByRef.get(
      stringifyEntityRef(
        parseEntityRef(name, {
          defaultKind: 'System',
          defaultNamespace: 'default',
        }),
      ),
    );
    return entity && isSystem(entity) ? entity : undefined;
  };
  // Every product code the registry itself assigns, across all products.
  const curatedCodes = new Set(
    snapshot.registry.portfolios
      .flatMap(portfolio => portfolio.products)
      .flatMap(product => product.systems || [])
      .map(s => normalizeCode(s.productCode))
      .filter((code): code is string => Boolean(code)),
  );
  const curatedProducts = snapshot.registry.portfolios.flatMap(portfolio =>
    portfolio.products.map(source => {
      const technical = snapshot.technical?.products?.[source.id];
      const relationships: Relationship[] = [];
      const software: Software[] = (source.systems || []).map(s => {
        const family = snapshot.families?.find(
          f => f.productId === source.id && f.members.includes(s.name),
        );
        const ref = stringifyEntityRef(
          parseEntityRef(s.hubSystem || s.name, {
            defaultKind: 'System',
            defaultNamespace: 'default',
          }),
        );
        // The System the record names (hubSystem, else name) first: that link
        // is deliberate, and survives a catalog that still carries a product
        // code on the wrong System (PR-043 sat on univerus-dispatcher after it
        // was confirmed for unity-dispatcher). The product code is the
        // fallback, and outranks a stale "registered: false".
        const entity =
          (s.registered !== false ? entitiesByRef.get(ref) : undefined) ??
          byCode.get(normalizeCode(s.productCode) ?? '');
        const curated = curatedProvenance(s.provenance);
        return {
          ...s,
          id: s.name,
          label: shortName(s.title || s.name),
          familyId: family?.id || s.name,
          familyName: family?.name || shortName(s.title || s.name),
          entityRef: entity ? stringifyEntityRef(entity) : undefined,
          offeringIds: [],
          repositoryIds: [],
          fieldProvenance: {
            label: curated,
            ...(s.productCode ? { productCode: curated } : {}),
          },
        };
      });
      // Catalog values win where one record joins one System. Versions that
      // share a System keep their own titles and codes, or they would read
      // alike; so does a record whose code differs from its System's.
      const joins = new Map<string, number>();
      for (const s of software)
        if (s.entityRef)
          joins.set(s.entityRef, (joins.get(s.entityRef) ?? 0) + 1);
      for (const s of software) {
        const entity = s.entityRef ? entitiesByRef.get(s.entityRef) : undefined;
        if (!entity || joins.get(s.entityRef!)! > 1) continue;
        const code = annotation(entity, CATALOG_ANNOTATIONS.productCode);
        if (
          code &&
          s.productCode &&
          normalizeCode(code) !== normalizeCode(s.productCode)
        )
          continue;
        // A catalog code another curated record already holds is a conflict
        // to resolve, not a value to copy: the record keeps no code, so it
        // still asks for one in Review.
        const claimedElsewhere =
          code && !s.productCode && curatedCodes.has(normalizeCode(code) ?? '');
        const title = entity.metadata.title?.trim();
        if (title) {
          s.label = shortName(title);
          s.fieldProvenance.label = catalogProvenance(entity);
        }
        if (code && !claimedElsewhere) {
          s.productCode = code;
          s.fieldProvenance.productCode = catalogProvenance(entity);
        }
      }
      const offerings = (source.modules || []).map(offering => {
        const softwareIds = (offering.systems || []).map(link =>
          typeof link === 'string' ? link : link.name,
        );
        for (const link of offering.systems || []) {
          const targetId = typeof link === 'string' ? link : link.name;
          // Plain links inherit attribution, never another record's confirmation.
          const provenance =
            typeof link === 'string'
              ? {
                  ...offering.provenance,
                  source:
                    offering.provenance?.source ||
                    source.provenance?.source ||
                    'Product registry',
                  verification: 'extracted' as const,
                  lastReviewed: null,
                }
              : link.provenance;
          relationships.push({
            id: `${source.id}|delivers|${offering.id}|${targetId}`,
            sourceId: offering.id,
            targetId,
            type: 'delivers',
            evidence: evidenceOf(provenance),
            provenance,
          });
        }
        return { ...offering, softwareIds };
      });
      const repoMap = new Map<string, Repository>();
      for (const raw of technical?.repositories || []) {
        const id = repositoryId(raw);
        const softwareIds = [
          ...new Set([
            ...(repoMap.get(id)?.softwareIds || []),
            ...(raw.systems || []),
          ]),
        ];
        const adoKey =
          raw.home === 'ado'
            ? adoRepoKey(raw.adoSource || raw.slug)
            : undefined;
        const entityRefs = entities
          .filter(entity => {
            const ann = entity.metadata.annotations || {};
            if (entity.kind.toLowerCase() !== 'component') return false;
            if (raw.home === 'ado')
              return !!adoKey && matchesAdoRepo(ann, adoKey);
            return (
              ann['github.com/project-slug']?.toLowerCase() ===
              raw.slug.toLowerCase()
            );
          })
          .map(entity => stringifyEntityRef(entity));
        repoMap.set(id, {
          ...raw,
          id,
          label: raw.slug.split('/').pop() || raw.slug,
          sourceUrl: repositoryUrl(raw),
          entityRefs,
          softwareIds,
          branchObservation: snapshot.branches?.repos?.[raw.slug],
        });
        for (const targetId of raw.systems || []) {
          const edgeId = `${source.id}|builds|${id}|${targetId}`;
          if (!relationships.some(edge => edge.id === edgeId))
            relationships.push({
              id: edgeId,
              sourceId: id,
              targetId,
              type: 'builds',
              evidence: evidenceOf(raw.provenance),
              provenance: raw.provenance,
            });
        }
      }
      const repositories = [...repoMap.values()].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      for (const s of software) {
        s.offeringIds = offerings
          .filter(o => o.softwareIds.includes(s.id))
          .map(o => o.id);
        s.repositoryIds = repositories
          .filter(r => r.softwareIds.includes(s.id))
          .map(r => r.id);
      }
      software.sort(
        (a, b) =>
          a.familyName.localeCompare(b.familyName) || compareVersions(a, b),
      );
      const questions: Question[] = relationships
        .filter(edge => edge.evidence !== 'confirmed')
        .map(edge => {
          const target = software.find(s => s.id === edge.targetId);
          const origin =
            edge.type === 'builds'
              ? repositories.find(r => r.id === edge.sourceId)?.label
              : offerings.find(o => o.id === edge.sourceId)?.name;
          return {
            id: edge.id,
            topic:
              edge.type === 'builds' ? 'Repository links' : 'Offering links',
            title:
              edge.type === 'builds'
                ? `Does ${origin || edge.sourceId} build ${
                    target?.label || edge.targetId
                  }?`
                : `Does ${target?.label || edge.targetId} deliver ${
                    origin || edge.sourceId
                  }?`,
            detail: target?.productCode || 'Product code not recorded',
            source: edge.provenance?.source || 'Product registry',
            revision: questionRevision(
              edge.id,
              edge.type,
              edge.sourceId,
              edge.targetId,
              origin,
              target?.label,
              target?.productCode,
              ...provenanceParts(edge.provenance),
            ),
            softwareIds: [edge.targetId],
            relationshipId: edge.id,
          };
        });
      for (const s of software.filter(row => !row.productCode))
        questions.push({
          id: `${source.id}|code|${s.id}`,
          topic: 'Software records',
          title: `What product code belongs to ${s.label}?`,
          detail: s.note || 'No product code is recorded.',
          source: s.provenance?.source || 'Product registry',
          revision: questionRevision(
            'code',
            s.id,
            s.label,
            s.productCode,
            s.note,
            ...provenanceParts(s.provenance),
          ),
          softwareIds: [s.id],
        });
      const releaseSource = snapshot.releases?.products?.[source.id];
      const documents = Object.entries(technical?.links || {})
        .filter(([, url]) => safeWebUrl(url))
        .map(([id, url]) => ({
          id,
          title:
            id === 'architecture' ? 'Architecture overview' : shortName(id),
          purpose: id === 'architecture' ? 'Architecture' : 'Development',
          url,
          softwareIds: software
            .filter(s => url.includes(`/${s.hubSystem || s.name}/`))
            .map(s => s.id),
        }));
      // The product's own System: the one its catalogName names, else the one
      // carrying its product code (same order as software records).
      const entity =
        (source.catalogName ? systemNamed(source.catalogName) : undefined) ??
        byCode.get(normalizeCode(source.productCode) ?? '');
      const join = <T>(catalog: T | undefined, curated: T | undefined) =>
        joinField(catalog, curated, entity, source.provenance);
      const name = join(entity?.metadata.title?.trim(), source.name);
      const tagline = join(
        entity?.metadata.description?.trim(),
        source.tagline,
      );
      const website = join(
        annotation(entity, CATALOG_ANNOTATIONS.website),
        source.website,
      );
      const owner = join(
        typeof entity?.spec?.owner === 'string'
          ? entity.spec.owner.trim()
          : undefined,
        source.owner,
      );
      const contact = join(
        annotation(entity, CATALOG_ANNOTATIONS.contact),
        source.contact,
      );
      const brand = join(
        annotation(entity, CATALOG_ANNOTATIONS.brand),
        undefined,
      );
      const fields = { name, tagline, website, owner, contact, brand };
      return {
        ...source,
        name: name.value ?? source.name,
        tagline: tagline.value,
        website: website.value,
        owner: owner.value,
        contact: contact.value,
        brand: brand.value,
        entityRef: entity ? stringifyEntityRef(entity) : undefined,
        fieldProvenance: Object.fromEntries(
          Object.entries(fields)
            .filter(([, field]) => field.provenance)
            .map(([key, field]) => [key, field.provenance]),
        ),
        label: shortName(name.value ?? source.name),
        portfolioId: portfolio.id,
        portfolioName: shortName(portfolio.name),
        software,
        offerings,
        repositories,
        relationships,
        questions,
        documents,
        releases:
          releaseSource?.status === 'connected'
            ? releaseSource.releases || []
            : [],
        releaseSourceStatus: releaseSource?.status || 'not-connected',
        stack: technical?.stack,
      };
    }),
  );
  return [
    ...curatedProducts,
    ...catalogOnlyProducts(snapshot, entities, curatedProducts),
  ].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Catalog Systems with a product code that no registry record joins, so a
 * product registered in the catalog shows up without editing the file. Each
 * sits in the portfolio of its Domain (spec.domain), in a group named after
 * the Domain when no portfolio claims it, or in "From the catalog".
 */
function catalogOnlyProducts(
  snapshot: Snapshot,
  entities: Entity[],
  products: Product[],
): Product[] {
  const claimed = new Set(
    products.flatMap(p => [p.entityRef, ...p.software.map(s => s.entityRef)]),
  );
  const ids = new Set(products.map(p => p.id));
  const domains = new Map(
    entities
      .filter(entity => entity.kind.toLowerCase() === 'domain')
      .map(entity => [stringifyEntityRef(entity), entity]),
  );
  return entities
    .filter(
      entity => isSystem(entity) && !claimed.has(stringifyEntityRef(entity)),
    )
    .flatMap(entity => {
      const code = annotation(entity, CATALOG_ANNOTATIONS.productCode);
      if (!code) return [];
      const ref = stringifyEntityRef(entity);
      const {
        name,
        namespace = 'default',
        title,
        description,
      } = entity.metadata;
      let domain: Entity | undefined;
      if (typeof entity.spec?.domain === 'string')
        try {
          domain = domains.get(
            stringifyEntityRef(
              parseEntityRef(entity.spec.domain, {
                defaultKind: 'Domain',
                defaultNamespace: namespace,
              }),
            ),
          );
        } catch {
          domain = undefined;
        }
      const portfolio =
        (domain &&
          snapshot.registry.portfolios.find(
            p =>
              p.catalogName?.toLowerCase() ===
              domain!.metadata.name.toLowerCase(),
          )) ||
        (domain && {
          id: `catalog-${domain.metadata.name}`,
          name: domain.metadata.title || domain.metadata.name,
        }) ||
        CATALOG_PORTFOLIO;
      const provenance = catalogProvenance(entity);
      const displayName = title?.trim() || name;
      const fields = {
        name: displayName,
        tagline: description?.trim() || undefined,
        website: annotation(entity, CATALOG_ANNOTATIONS.website),
        owner:
          typeof entity.spec?.owner === 'string'
            ? entity.spec.owner.trim() || undefined
            : undefined,
        contact: annotation(entity, CATALOG_ANNOTATIONS.contact),
        brand: annotation(entity, CATALOG_ANNOTATIONS.brand),
      };
      // Readable and stable in links; never the id of a registry product.
      let id = namespace === 'default' ? name : `${namespace}-${name}`;
      if (ids.has(id)) id = `catalog-${id}`;
      const software: Software = {
        name,
        title: displayName,
        productCode: code,
        provenance,
        id: name,
        label: shortName(displayName),
        familyId: name,
        familyName: shortName(displayName),
        entityRef: ref,
        offeringIds: [],
        repositoryIds: [],
        fieldProvenance: { label: provenance, productCode: provenance },
      };
      const product: Product = {
        id,
        productCode: code,
        ...fields,
        provenance,
        entityRef: ref,
        fieldProvenance: Object.fromEntries(
          Object.entries(fields)
            .filter(([, value]) => value)
            .map(([key]) => [key, provenance]),
        ),
        label: shortName(displayName),
        portfolioId: portfolio.id,
        portfolioName: shortName(portfolio.name),
        software: [software],
        offerings: [],
        repositories: [],
        relationships: [],
        questions: [],
        documents: [],
        releases: [],
        releaseSourceStatus: 'not-connected',
      };
      return [product];
    });
}
export function scopedSoftware(product: Product, scope: Scope): Software[] {
  const term = scope.search.trim().toLowerCase();
  return product.software.filter(s => {
    if (scope.software.length && !scope.software.includes(s.id)) return false;
    if (
      scope.offerings.length &&
      !s.offeringIds.some(id => scope.offerings.includes(id))
    )
      return false;
    if (!term) return true;
    return [
      s.label,
      s.productCode || '',
      s.familyName,
      ...(s.uemModules || []),
      ...product.offerings
        .filter(o => s.offeringIds.includes(o.id))
        .map(o => o.name),
      ...product.repositories
        .filter(r => s.repositoryIds.includes(r.id))
        .map(r => r.label),
    ]
      .join(' ')
      .toLowerCase()
      .includes(term);
  });
}
export function scopedRepositories(
  product: Product,
  scope: Scope,
): Repository[] {
  const selected = new Set(scopedSoftware(product, scope).map(s => s.id));
  const constrained = !!(
    scope.software.length ||
    scope.offerings.length ||
    scope.search.trim()
  );
  return product.repositories.filter(
    r =>
      !constrained ||
      r.softwareIds.some(id => selected.has(id)) ||
      (!scope.software.length &&
        !scope.offerings.length &&
        r.label.toLowerCase().includes(scope.search.toLowerCase())),
  );
}
export const branchRoles = [
  {
    id: 'development',
    label: 'Development',
    names: ['development', 'develop', 'dev', 'devel'],
  },
  {
    id: 'staging',
    label: 'Staging',
    names: [
      'staging',
      'stage',
      'stg',
      'uat',
      'qa',
      'pre-release',
      'prerelease',
      'preprod',
      'pre-prod',
    ],
  },
  {
    id: 'production',
    label: 'Production',
    names: [
      'main',
      'master',
      'production',
      'prod',
      'live',
      'stable',
      'release',
    ],
  },
];
export function branchSummary(repo: Repository) {
  const observation = repo.branchObservation;
  const known = !!observation && !observation.error;
  const roles = branchRoles.map(role => ({
    ...role,
    matches: known
      ? observation!.branches.filter(
          branch =>
            role.names.includes(branch.toLowerCase()) ||
            (role.id === 'production' &&
              /^(release|stable|production)[-\/_]/i.test(branch)) ||
            (role.id === 'staging' && /^pre-release[-\/_]/i.test(branch)),
        )
      : [],
  }));
  return {
    known,
    roles,
    found: roles.filter(role => role.matches.length).length,
    error: observation?.error,
  };
}
export function reviewCsv(
  questions: Question[],
  answers: ReviewAnswer[],
): string {
  const field = (value: string) =>
    `"${(/^[\s]*[=+@\-\t\r]/.test(value) ? `'${value}` : value).replace(
      /"/g,
      '""',
    )}"`;
  const lines = [
    [
      'question_id',
      'question',
      'source_revision',
      'answer',
      'correction',
      'respondent',
      'answered_at',
      'state',
    ],
  ];
  for (const answer of answers) {
    const q = questions.find(question => question.id === answer.questionId);
    let state = 'orphaned-draft';
    if (q) state = answer.revision === q.revision ? 'draft' : 'stale-draft';
    lines.push([
      answer.questionId,
      q?.title || 'Question no longer in current source',
      answer.revision,
      answer.answer,
      answer.correction || '',
      answer.respondent,
      answer.answeredAt,
      state,
    ]);
  }
  return lines.map(line => line.map(field).join(',')).join('\r\n');
}

/**
 * The owner's full catalog reference, or null when no real owner is recorded.
 * Placeholders such as "unassigned" and malformed values count as no owner,
 * so pages never link to a group that does not exist.
 */
export function ownerRef(owner?: string | null): string | null {
  const value = owner?.trim();
  if (!value || /^(unassigned|none|tbd|n\/a|unknown)$/i.test(value))
    return null;
  try {
    return stringifyEntityRef(
      parseEntityRef(value, {
        defaultKind: 'group',
        defaultNamespace: 'default',
      }),
    );
  } catch {
    return null;
  }
}
